import React, { useState } from 'react';
import { Button } from '@ui/button';
import { useAppSelector, useMediaQuery } from '@core';
import { selectedSeatsSelector, seatPreviewAvailableSelector } from '../store/selectors';
import type { SelectedSeat } from '../store/slice';
import { BinocularIcon } from './BinocularIcon';
import { SeatViewDialog } from './SeatViewDialog';
import { formatSectorWithVisibility } from '../utils/formatSeatVisibility';

interface SeatCartProps {
  onCheckout: () => void;
  onRemoveSeat: (seatId: number) => void;
}

export const SeatCart: React.FC<SeatCartProps> = ({ onCheckout, onRemoveSeat }) => {
  const selectedSeats = useAppSelector(selectedSeatsSelector);
  const canShowSeatPreview = useAppSelector(seatPreviewAvailableSelector);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const totalPrice = selectedSeats.reduce(
    (sum: number, seat: SelectedSeat) => sum + (seat.price || 0),
    0
  );
  const [previewSeat, setPreviewSeat] = useState<SelectedSeat | null>(null);

  const handleOpenPreview = (seat: SelectedSeat) => {
    if (!canShowSeatPreview || !seat.previewImageUrl) return;
    setPreviewSeat(seat);
  };

  const handleClosePreview = () => setPreviewSeat(null);

  if (selectedSeats.length === 0) {
    return null;
  }

  // Desktop: collapsed cart with hover reveal. Mobile: keep full cart always visible.
  if (!isMobile) {
    return (
      <div
        className="fixed bottom-0 left-0 right-0 z-50 border-t border-separator bg-[color:var(--surface-elevated)] shadow-[var(--shadow-lg)] backdrop-blur-sm transition-shadow duration-300 group group-hover:shadow-[var(--shadow-xl)]"
        tabIndex={0}
      >
        <div className="max-w-7xl mx-auto">
          {/* Hidden by default; smooth slide+fade on hover/focus */}
          <div className="pointer-events-none max-h-0 opacity-0 overflow-hidden transform-gpu scale-[0.98] transition-[max-height,opacity,transform] duration-400 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:pointer-events-auto group-focus-within:pointer-events-auto group-hover:max-h-[140px] group-hover:opacity-100 group-hover:scale-100 group-focus-within:max-h-[140px] group-focus-within:opacity-100 group-focus-within:scale-100 group-hover:overflow-visible group-focus-within:overflow-visible">
            <div className="flex -space-x-3 overflow-x-auto pb-1 px-3 pt-2 -mt-1">
              {selectedSeats.map((seat: SelectedSeat, index: number) => (
                <div
                  key={seat.seatId}
                  data-seat-id={seat.seatId}
                  className="group/item relative w-36 flex-shrink-0 rounded-lg border border-separator bg-surface-card px-3 pb-1 pt-2 shadow-[var(--shadow-sm)] transition-transform duration-200 ease-out hover:-translate-y-1 hover:shadow-[var(--shadow-lg)]"
                  style={{
                    zIndex: selectedSeats.length - index,
                    transform: `translateX(${index * -2}px)`,
                    marginLeft: index > 0 ? '-2px' : '0',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.zIndex = '9999';
                    e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.zIndex = (selectedSeats.length - index).toString();
                    e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                  }}
                >
                  <div className="space-y-1 w-full">
                    <div className="flex justify-between items-start">
                      <p className="text-xs font-medium text-text-primary">
                        Ряд {seat.rowName}, Место {seat.seatNumber}
                      </p>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          onRemoveSeat(seat.seatId);
                        }}
                        className="cursor-pointer text-text-secondary opacity-0 transition-opacity duration-200 hover:text-status-error focus:outline-none group-hover/item:opacity-100"
                        aria-label="Удалить место"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                    <p className="text-sm text-text-secondary">
                      {formatSectorWithVisibility(seat.sectorName, seat.visibility)}
                    </p>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-action-primary">
                        {seat.price.toLocaleString()} ₽
                      </p>
                      {canShowSeatPreview && (
                        <BinocularIcon
                          className="h-12 w-12 opacity-0 transition-opacity duration-200 group-hover/item:opacity-100 cursor-pointer"
                          onClick={() => {
                            handleOpenPreview(seat);
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Always-visible compact summary bar */}
          <div className="flex items-center justify-between px-12 py-1 transition-colors duration-200 group-hover:bg-surface-card">
            <div>
              <p className="text-base font-medium">Итого: {totalPrice.toLocaleString()} ₽</p>
              <p className="text-xs text-text-muted">{selectedSeats.length} мест(а)</p>
            </div>
            <Button
              onClick={onCheckout}
              className="h-10 cursor-pointer bg-action-primary px-4 text-action-primary-foreground transition-transform duration-200 hover:bg-action-primary-hover group-hover:translate-y-[-1px]"
            >
              Перейти к оформлению
            </Button>
          </div>
        </div>
        <SeatViewDialog
          open={Boolean(previewSeat)}
          seat={previewSeat}
          onClose={handleClosePreview}
        />
      </div>
    );
  }

  // Mobile fallback: original full cart
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-separator bg-surface-card p-1 shadow-[var(--shadow-lg)]">
      <div className="max-w-7xl mx-auto">
        <div className="flex -space-x-4 mb-1 overflow-x-auto pb-1 px-1">
          {selectedSeats.map((seat: SelectedSeat, index: number) => (
            <div
              key={seat.seatId}
              data-seat-id={seat.seatId}
              className="group relative w-30 flex-shrink-0 rounded-lg border border-separator bg-surface-card px-1.5 pb-1 pt-1 shadow-[var(--shadow-sm)] transition-all duration-200 ease-in-out hover:-translate-y-2 hover:shadow-[var(--shadow-lg)]"
              style={{
                zIndex: selectedSeats.length - index,
                transform: `translateX(${index * -2}px)`,
                marginLeft: index > 0 ? '-2px' : '0',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.zIndex = '9999';
                e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.zIndex = (selectedSeats.length - index).toString();
                e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
              }}
            >
              <div className="space-y-1 w-full">
                <div className="flex justify-between items-start">
                  <p className="text-xs font-medium text-text-primary">
                    Ряд {seat.rowName}, Место {seat.seatNumber}
                  </p>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      onRemoveSeat(seat.seatId);
                    }}
                    className="cursor-pointer text-text-secondary opacity-100 transition-opacity duration-200 hover:text-status-error focus:outline-none"
                    aria-label="Удалить место"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3.5 w-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <p className="text-xs text-text-secondary">
                  {formatSectorWithVisibility(seat.sectorName, seat.visibility)}
                </p>
                <p className="text-xs font-medium text-action-primary">
                  {seat.price.toLocaleString()} ₽
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mx-auto flex w-full max-w-sm items-center justify-between border-t border-separator px-1 pt-1">
          <div>
            <p className="text-base font-medium">Итого: {totalPrice.toLocaleString()} ₽</p>
            <p className="text-xs text-text-muted">{selectedSeats.length} мест(а)</p>
          </div>
          <Button
            onClick={onCheckout}
            className="h-9 cursor-pointer bg-action-primary px-3 py-6 text-sm text-action-primary-foreground hover:bg-action-primary-hover"
          >
            Перейти к оформлению
          </Button>
        </div>
      </div>
    </div>
  );
};
